import {IconButton} from '@dropbox/dig-components/buttons';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine, InfoLine} from '@dropbox/dig-icons/assets';
import type {WorkstreamMetric} from 'client';
import {WorkstreamMetricVisibility} from 'client';
import {CompactMetricValueText} from 'components/DSYS/CompactMetricValueText';
import {Link} from 'components/DSYS/Link';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {Column, Table} from 'components/DSYS/Table';
import {tableStickyLayoutDataProps} from 'components/DSYS/tableStickyLayout';
import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {RedactedMetricValuePlaceholder} from 'views/workstreams/detail/RedactedMetricValuePlaceholder';
import {MetricStatusControl} from 'views/workstreams/detail/WorkstreamRedesignSections';

import {
  getTrendBackgroundColor,
  type ScorecardMetric,
  type ScorecardSection,
} from './scorecardData';
import {type ForecastComparisonTone, getForecastComparisonTone} from './scorecardDirection';
import {ScorecardMetricNameCell, scorecardWorkstreamPath} from './ScorecardMetricNameCell';
import {ScorecardNotesEditor} from './ScorecardNotesEditor';
import styles from './ScorecardsTable.module.css';
import {ScorecardWorkstreamStatusBadge} from './ScorecardWorkstreamStatusBadge';

type ScorecardMetricRowProps = {
  metric: ScorecardMetric;
  snapshotIndex: number;
  snapshotKey?: string | null;
  priorSnapshotIndex: number;
  priorDateLabel: string | null;
  isEndOfQuarter: boolean;
  showCurrentUpdatedAt: boolean;
  notesExpanded: boolean;
  onNotesExpandedChange: (expanded: boolean) => void;
};

type ScorecardSectionRowProps = {
  section: ScorecardSection;
  columnCount: number;
  metric?: ScorecardMetric;
  hasChildRows: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  snapshotIndex: number;
  snapshotKey?: string | null;
  priorSnapshotIndex: number;
  priorDateLabel: string | null;
  isEndOfQuarter: boolean;
  showCurrentUpdatedAt: boolean;
  notesExpanded?: boolean;
  onNotesExpandedChange?: (expanded: boolean) => void;
};

type ScorecardMetricDataCellsProps = {
  metric: ScorecardMetric;
  snapshotIndex: number;
  snapshotKey?: string | null;
  priorSnapshotIndex: number;
  priorDateLabel: string | null;
  isEndOfQuarter: boolean;
  showCurrentUpdatedAt: boolean;
  notesExpanded: boolean;
  onNotesExpandedChange: (expanded: boolean) => void;
};

const parseScorecardSectionWorkstreamId = (sectionId: string): number | undefined => {
  const parsed = Number(sectionId);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const FORECAST_COMPARISON_TREND = {good: 'up', bad: 'down'} as const;

const getForecastComparisonBackgroundColor = (tone: ForecastComparisonTone | undefined) =>
  tone ? getTrendBackgroundColor(FORECAST_COMPARISON_TREND[tone]) : undefined;

const formatMetricUpdatedAt = (value?: string): string => {
  if (!value) {
    return '--';
  }
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const formatMetricUpdatedAtTooltip = (value?: string | null): string | undefined => {
  const formatted = formatMetricUpdatedAt(value ?? undefined);
  return formatted === '--' ? undefined : `Last updated ${formatted}`;
};

const buildGroupedScorecardSections = (section: ScorecardSection): ScorecardSection[] => {
  if (!section.grouped || section.metrics.length === 0) {
    return [section];
  }

  const metricsByGroup = new Map<string, ScorecardMetric[]>();
  const labelsByGroup = new Map<string, string>();

  for (const metric of section.metrics) {
    const groupId = metric.groupId ?? metric.sourceWorkstreamId?.toString() ?? section.id;
    const groupMetrics = metricsByGroup.get(groupId);
    if (groupMetrics) {
      groupMetrics.push(metric);
    } else {
      metricsByGroup.set(groupId, [metric]);
    }
    if (!labelsByGroup.has(groupId)) {
      labelsByGroup.set(groupId, metric.groupLabel ?? metric.sourceWorkstreamName ?? section.label);
    }
  }

  return Array.from(metricsByGroup.entries()).map(([groupId, groupMetrics]) => ({
    ...section,
    id: groupId,
    label: labelsByGroup.get(groupId) ?? section.label,
    metrics: groupMetrics,
  }));
};

const DEFAULT_EXPANDED_GROUPED_SCORECARD_SECTION_IDS = new Set(['ga']);

const getGroupedScorecardSectionExpansionKey = (sectionId: string, groupId: string): string =>
  `${sectionId}:${groupId}`;

const getDefaultExpandedScorecardSectionKeys = (
  sections: ScorecardSection[],
  showSectionHeaders: boolean
): string[] => {
  if (!showSectionHeaders) {
    return [];
  }

  return sections.flatMap((section) => {
    if (!DEFAULT_EXPANDED_GROUPED_SCORECARD_SECTION_IDS.has(section.id)) {
      return [];
    }
    return buildGroupedScorecardSections(section).map((group) =>
      getGroupedScorecardSectionExpansionKey(section.id, group.id)
    );
  });
};

const getScorecardNotesExpansionKey = (
  metric: ScorecardMetric,
  snapshotIndex: number,
  snapshotKey?: string | null
): string =>
  `${metric.sectionId}:${metric.id}:${snapshotKey ?? metric.snapshotDts[snapshotIndex] ?? snapshotIndex}`;

const ScorecardMetricCurrentUpdatedAtCell = ({
  currentUpdatedAt,
  redacted = false,
}: {
  currentUpdatedAt?: string;
  redacted?: boolean;
}) => (
  <Table.Cell style={{padding: 0}}>
    <Box width="100%" minHeight="100%" padding="8" display="flex" alignItems="center">
      {redacted ? (
        <RedactedMetricValuePlaceholder />
      ) : (
        <Text size="small" color={currentUpdatedAt ? undefined : 'subtle'}>
          {formatMetricUpdatedAt(currentUpdatedAt)}
        </Text>
      )}
    </Box>
  </Table.Cell>
);

const ScorecardMetricDataCells = ({
  metric,
  snapshotIndex,
  snapshotKey,
  priorSnapshotIndex,
  priorDateLabel,
  isEndOfQuarter,
  showCurrentUpdatedAt,
  notesExpanded,
  onNotesExpandedChange,
}: ScorecardMetricDataCellsProps) => {
  const isRedacted = metric.visibility === WorkstreamMetricVisibility.REDACTED;
  const currentValue =
    (isEndOfQuarter ? metric.quarterActuals[snapshotIndex] : metric.values[snapshotIndex]) ?? null;
  const currentQuarterBudget = metric.currentQuarterBudgets[snapshotIndex] ?? null;
  const currentQuarterTarget = metric.currentQuarterTargets[snapshotIndex] ?? null;
  const eoyBudget = metric.eoyBudgets[snapshotIndex] ?? null;
  // End-of-quarter rows highlight budget and target by whether the quarter's
  // actual beat them in the metric's desired direction.
  const desiredDirection =
    isEndOfQuarter && !isRedacted ? (metric.desiredDirection ?? 'unknown') : null;
  const budgetBackgroundColor = desiredDirection
    ? getForecastComparisonBackgroundColor(
        getForecastComparisonTone(currentValue, currentQuarterBudget, desiredDirection)
      )
    : undefined;
  const targetBackgroundColor = desiredDirection
    ? getForecastComparisonBackgroundColor(
        getForecastComparisonTone(currentValue, currentQuarterTarget, desiredDirection)
      )
    : undefined;
  const isCurrentSnapshot =
    metric.snapshotDts[snapshotIndex] === 'current' || snapshotKey === 'current';
  const priorValue =
    priorSnapshotIndex < metric.values.length ? (metric.values[priorSnapshotIndex] ?? null) : null;
  const currentUpdatedAt = metric.currentUpdatedAts[snapshotIndex] ?? null;
  const priorCurrentUpdatedAt =
    priorSnapshotIndex < metric.currentUpdatedAts.length
      ? (metric.currentUpdatedAts[priorSnapshotIndex] ?? null)
      : null;
  const currentSnapshotId = metric.snapshotIds[snapshotIndex] ?? null;

  const renderValueCell = (
    value: string | null,
    updatedAtTooltip?: string,
    backgroundColor?: ReturnType<typeof getForecastComparisonBackgroundColor>
  ) => {
    const valueNode = (
      <span className={updatedAtTooltip ? undefined : styles.metricValueTooltipTrigger}>
        <CompactMetricValueText value={value} showTooltip={false} />
      </span>
    );

    return (
      <Table.Cell style={{padding: 0}}>
        <Box
          width="100%"
          minHeight="100%"
          padding="8"
          display="flex"
          alignItems="center"
          backgroundColor={backgroundColor}
        >
          {isRedacted ? (
            <RedactedMetricValuePlaceholder />
          ) : (
            <OptionalTooltip
              enabled={Boolean(updatedAtTooltip)}
              title={updatedAtTooltip}
              placement="top"
              wrapperClassName={styles.metricValueTooltipTrigger}
            >
              {valueNode}
            </OptionalTooltip>
          )}
        </Box>
      </Table.Cell>
    );
  };

  const shouldShowCurrentValueUpdatedAtTooltips = !isCurrentSnapshot && !isEndOfQuarter;

  return (
    <>
      <Table.Cell style={{padding: 0}}>
        <Box width="100%" minHeight="100%" padding="8" display="flex" alignItems="center">
          {isRedacted ? (
            <RedactedMetricValuePlaceholder />
          ) : (
            <MetricStatusControl
              metric={
                {id: metric.id, status: metric.status ?? 'no_status'} as unknown as WorkstreamMetric
              }
            />
          )}
        </Box>
      </Table.Cell>
      {renderValueCell(
        currentValue,
        shouldShowCurrentValueUpdatedAtTooltips
          ? formatMetricUpdatedAtTooltip(currentUpdatedAt)
          : undefined
      )}
      {priorDateLabel
        ? renderValueCell(
            priorValue,
            shouldShowCurrentValueUpdatedAtTooltips
              ? formatMetricUpdatedAtTooltip(priorCurrentUpdatedAt)
              : undefined
          )
        : null}
      {renderValueCell(currentQuarterBudget, undefined, budgetBackgroundColor)}
      {renderValueCell(currentQuarterTarget, undefined, targetBackgroundColor)}
      {renderValueCell(eoyBudget)}
      {showCurrentUpdatedAt ? (
        <ScorecardMetricCurrentUpdatedAtCell
          currentUpdatedAt={metric.currentUpdatedAt}
          redacted={isRedacted}
        />
      ) : null}
      <Table.Cell className={styles.scorecardNotesTableCell} style={{padding: 0}}>
        {isRedacted ? (
          <Box width="100%" minHeight="100%" padding="8" display="flex" alignItems="center">
            <RedactedMetricValuePlaceholder />
          </Box>
        ) : (
          <ScorecardNotesEditor
            key={`scorecard-notes-${metric.id}-${snapshotKey ?? snapshotIndex}`}
            metricId={metric.id}
            metricSnapshotId={currentSnapshotId}
            isCurrentSnapshot={isCurrentSnapshot}
            initialNotes={metric.periodNotes[snapshotIndex] ?? null}
            notesExpanded={notesExpanded}
            onNotesExpandedChange={onNotesExpandedChange}
          />
        )}
      </Table.Cell>
    </>
  );
};

const ScorecardSectionRow = ({
  section,
  columnCount,
  metric,
  hasChildRows,
  isExpanded,
  onToggleExpanded,
  snapshotIndex,
  snapshotKey,
  priorSnapshotIndex,
  priorDateLabel,
  isEndOfQuarter,
  showCurrentUpdatedAt,
  notesExpanded = false,
  onNotesExpandedChange,
}: ScorecardSectionRowProps) => {
  const shouldIgnoreToggleEvent = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    Boolean(
      target.closest(
        'a, button, input, select, textarea, [contenteditable="true"], [role="textbox"]'
      )
    );

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!hasChildRows || shouldIgnoreToggleEvent(event.target)) {
      return;
    }
    onToggleExpanded();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!hasChildRows || shouldIgnoreToggleEvent(event.target)) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggleExpanded();
    }
  };
  const sectionWorkstreamPath = scorecardWorkstreamPath(
    parseScorecardSectionWorkstreamId(section.id)
  );
  const sectionLabel = sectionWorkstreamPath ? (
    <Link to={sectionWorkstreamPath} monochromatic hasNoUnderline showUnderlineOnHover>
      {section.label}
    </Link>
  ) : (
    section.label
  );

  return (
    <Table.Row
      key={`section-row-${section.id}`}
      className={styles.pillarSectionRow}
      tabIndex={hasChildRows ? 0 : undefined}
      role={hasChildRows ? 'button' : undefined}
      aria-expanded={hasChildRows ? isExpanded : undefined}
      onKeyDown={handleKeyDown}
    >
      <Table.Cell style={{padding: 0}}>
        <Box
          width="100%"
          minHeight="100%"
          padding="8"
          display="flex"
          alignItems="center"
          className={hasChildRows ? styles.sectionToggleCellContent : undefined}
          onClick={handleClick}
        >
          <Box display="flex" alignItems="center" style={{gap: 8, minWidth: 0}}>
            {metric ? (
              <ScorecardMetricNameCell
                metric={metric}
                snapshotKey={snapshotKey}
                disclosure={
                  hasChildRows ? (
                    <UIIcon
                      src={isExpanded ? ChevronDownLine : ChevronRightLine}
                      size="small"
                      color="subtle"
                      aria-hidden
                    />
                  ) : null
                }
                fallbackWorkstreamId={parseScorecardSectionWorkstreamId(section.id)}
                fallbackWorkstreamName={section.label}
              />
            ) : (
              <>
                {hasChildRows ? (
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    className={styles.metricDisclosureIcon}
                  >
                    <UIIcon
                      src={isExpanded ? ChevronDownLine : ChevronRightLine}
                      size="small"
                      color="subtle"
                      aria-hidden
                    />
                  </Box>
                ) : null}
                <Stack gap="2" style={{minWidth: 0}}>
                  <Box display="flex" alignItems="center" className={atoms({columnGap: '4'})}>
                    <ScorecardWorkstreamStatusBadge
                      context={
                        section.metrics.find(
                          (item) =>
                            item.sourceWorkstreamContext?.id ===
                            parseScorecardSectionWorkstreamId(section.id)
                        )?.sourceWorkstreamContext
                      }
                      snapshotKey={snapshotKey}
                    />
                    <Text
                      tagName="div"
                      size="xsmall"
                      color="subtle"
                      isBold
                      className={styles.metricWorkstreamSubtitle}
                      title={section.label}
                    >
                      {sectionLabel}
                    </Text>
                  </Box>
                </Stack>
              </>
            )}
          </Box>
        </Box>
      </Table.Cell>
      {metric ? (
        <ScorecardMetricDataCells
          metric={metric}
          snapshotIndex={snapshotIndex}
          snapshotKey={snapshotKey}
          priorSnapshotIndex={priorSnapshotIndex}
          priorDateLabel={priorDateLabel}
          isEndOfQuarter={isEndOfQuarter}
          showCurrentUpdatedAt={showCurrentUpdatedAt}
          notesExpanded={notesExpanded}
          onNotesExpandedChange={onNotesExpandedChange ?? (() => undefined)}
        />
      ) : (
        Array.from({length: columnCount - 1}, (_, index) => (
          <Table.Cell key={`section-row-${section.id}-empty-${index}`} style={{padding: 0}}>
            <Box width="100%" minHeight="100%" padding="8" display="flex" alignItems="center" />
          </Table.Cell>
        ))
      )}
    </Table.Row>
  );
};

const ScorecardMetricRow = ({
  metric,
  snapshotIndex,
  snapshotKey,
  priorSnapshotIndex,
  priorDateLabel,
  isEndOfQuarter,
  showCurrentUpdatedAt,
  notesExpanded,
  onNotesExpandedChange,
}: ScorecardMetricRowProps) => {
  return (
    <Table.Row
      key={`${metric.sectionId}:${metric.id}`}
      className={metric.isPillarNorthStar ? styles.northStarMetricRow : undefined}
    >
      <Table.Cell style={{padding: 0}}>
        <Box width="100%" minHeight="100%" padding="8" display="flex" alignItems="center">
          <ScorecardMetricNameCell metric={metric} snapshotKey={snapshotKey} />
        </Box>
      </Table.Cell>
      <ScorecardMetricDataCells
        metric={metric}
        snapshotIndex={snapshotIndex}
        snapshotKey={snapshotKey}
        priorSnapshotIndex={priorSnapshotIndex}
        priorDateLabel={priorDateLabel}
        isEndOfQuarter={isEndOfQuarter}
        showCurrentUpdatedAt={showCurrentUpdatedAt}
        notesExpanded={notesExpanded}
        onNotesExpandedChange={onNotesExpandedChange}
      />
    </Table.Row>
  );
};

const SCORECARD_LOADING_ROW_COUNT = 10;

type ScorecardsTableProps = {
  sections: ScorecardSection[];
  showSectionHeaders: boolean;
  snapshotIndex: number;
  snapshotKey?: string | null;
  currentDateLabel: string;
  priorDateLabel: string | null;
  currentQuarterLabel: string;
  isEndOfQuarter?: boolean;
  loading?: boolean;
  // 'self' when embedded in a pane that scrolls on its own (/workstreams).
  scrollMode?: 'self' | 'page';
};

export const ScorecardsTable = ({
  sections,
  showSectionHeaders,
  snapshotIndex,
  snapshotKey,
  currentDateLabel,
  priorDateLabel,
  isEndOfQuarter = false,
  currentQuarterLabel,
  loading = false,
  scrollMode = 'page',
}: ScorecardsTableProps) => {
  const priorSnapshotIndex = snapshotIndex + 1;
  const showCurrentUpdatedAt = useMemo(
    () =>
      snapshotKey === 'current' ||
      sections.some((section) =>
        section.metrics.some((metric) => metric.snapshotDts[snapshotIndex] === 'current')
      ),
    [sections, snapshotIndex, snapshotKey]
  );
  const effectivePriorDateLabel =
    showCurrentUpdatedAt && priorDateLabel ? 'Previous' : priorDateLabel;
  const showPriorColumn = Boolean(effectivePriorDateLabel);
  const [expandedSectionIds, setExpandedSectionIds] = useState<Set<string>>(() => new Set());
  const defaultExpandedSectionIdsRef = useRef<Set<string>>(new Set());
  const [expandedNotesRowIds, setExpandedNotesRowIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const defaultExpandedKeys = getDefaultExpandedScorecardSectionKeys(
      sections,
      showSectionHeaders
    );
    const uninitializedKeys = defaultExpandedKeys.filter(
      (key) => !defaultExpandedSectionIdsRef.current.has(key)
    );
    if (uninitializedKeys.length === 0) {
      return;
    }

    defaultExpandedSectionIdsRef.current = new Set([
      ...defaultExpandedSectionIdsRef.current,
      ...uninitializedKeys,
    ]);
    setExpandedSectionIds((current) => {
      const next = new Set(current);
      for (const key of uninitializedKeys) {
        next.add(key);
      }
      return next;
    });
  }, [sections, showSectionHeaders]);

  useEffect(() => {
    setExpandedNotesRowIds(new Set());
  }, [snapshotIndex, snapshotKey]);

  const columns = useMemo<Column<ScorecardMetric>[]>(() => {
    return [
      {
        type: 'metric',
        minWidth: 380,
        width: 380,
        // Scorecard rows are at least two text blocks tall (name + description),
        // so the skeleton renders two stacked lines to match real row height.
        skeleton: () => (
          <Stack gap="4">
            <Skeleton.Text width={180} />
            <Skeleton.Text width={240} />
          </Stack>
        ),
      },
      {type: 'status', minWidth: 100, width: 100},
      {type: 'current_period', minWidth: 120, width: 120},
      ...(showPriorColumn
        ? ([{type: 'prior_period', minWidth: 120, width: 120}] as Column<ScorecardMetric>[])
        : []),
      {type: 'current_quarter_budget', minWidth: 120, width: 120},
      {type: 'current_quarter_target', minWidth: 120, width: 120},
      {type: 'eoy_budget', minWidth: 120, width: 120},
      ...(showCurrentUpdatedAt
        ? ([{type: 'current_updated_at', minWidth: 120, width: 120}] as Column<ScorecardMetric>[])
        : []),
      {type: 'notes', minWidth: 280, spanner: true},
    ];
  }, [showCurrentUpdatedAt, showPriorColumn]);

  const columnLabelOverrides = useMemo(
    () => ({
      metric: 'metric',
      current_updated_at: 'Last updated',
      notes: 'notes',
      status: 'status',
    }),
    []
  );

  const renderHeaderLabel = useCallback((label: string, tooltip: string) => {
    return (
      <Box display="inline-flex" alignItems="center" style={{gap: 4}}>
        <Text isBold size="small">
          {label}
        </Text>
        <Tooltip title={tooltip} placement="top">
          <IconButton variant="borderless" size="small" aria-label={`${label} info`}>
            <UIIcon src={InfoLine} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, []);

  /** Dynamic labels (snapshot dates) must use columnHeaderLabel. */
  const columnHeaderLabel = useMemo(
    () => ({
      current_period: renderHeaderLabel(
        currentDateLabel,
        isEndOfQuarter
          ? 'The actual value recorded for this metric at the end of the quarter.'
          : 'The most recent value reported for this metric as of the selected reporting date.'
      ),
      ...(effectivePriorDateLabel
        ? {
            prior_period: renderHeaderLabel(
              effectivePriorDateLabel,
              'The value reported for this metric in the previous bi-weekly reporting period.'
            ),
          }
        : {}),
      current_quarter_budget: renderHeaderLabel(
        `${currentQuarterLabel} Goal`,
        `${currentQuarterLabel} goal is the committed plan number for the quarter — what we expect to hit.`
      ),
      current_quarter_target: renderHeaderLabel(
        `${currentQuarterLabel} Stretch goal`,
        `${currentQuarterLabel} stretch goal is the ambitious number for the quarter — above plan, achievable if things break our way.`
      ),
      eoy_budget: renderHeaderLabel(
        'EOY Goal',
        'EOY goal is the committed full-year plan number this metric is expected to reach by year end.'
      ),
    }),
    [
      currentDateLabel,
      currentQuarterLabel,
      effectivePriorDateLabel,
      isEndOfQuarter,
      renderHeaderLabel,
    ]
  );

  const tableData = useMemo(() => sections.flatMap((section) => section.metrics), [sections]);
  const columnCount = columns.length;

  const setMetricNotesExpanded = useCallback(
    (metric: ScorecardMetric, expanded: boolean) => {
      const expansionKey = getScorecardNotesExpansionKey(metric, snapshotIndex, snapshotKey);
      setExpandedNotesRowIds((current) => {
        const next = new Set(current);
        if (expanded) {
          next.add(expansionKey);
        } else {
          next.delete(expansionKey);
        }
        return next;
      });
    },
    [snapshotIndex, snapshotKey]
  );

  const renderRow = useCallback(
    (metric: ScorecardMetric) => {
      const notesExpansionKey = getScorecardNotesExpansionKey(metric, snapshotIndex, snapshotKey);
      return (
        <ScorecardMetricRow
          key={`${metric.sectionId}:${metric.id}`}
          metric={metric}
          snapshotIndex={snapshotIndex}
          snapshotKey={snapshotKey}
          priorSnapshotIndex={priorSnapshotIndex}
          priorDateLabel={effectivePriorDateLabel}
          isEndOfQuarter={isEndOfQuarter}
          showCurrentUpdatedAt={showCurrentUpdatedAt}
          notesExpanded={expandedNotesRowIds.has(notesExpansionKey)}
          onNotesExpandedChange={(expanded) => setMetricNotesExpanded(metric, expanded)}
        />
      );
    },
    [
      expandedNotesRowIds,
      effectivePriorDateLabel,
      isEndOfQuarter,
      priorSnapshotIndex,
      setMetricNotesExpanded,
      showCurrentUpdatedAt,
      snapshotIndex,
      snapshotKey,
    ]
  );

  const toggleSectionExpanded = useCallback((sectionId: string) => {
    setExpandedSectionIds((current) => {
      const next = new Set(current);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  return (
    <Box width="100%" {...tableStickyLayoutDataProps}>
      <Table
        columns={columns}
        data={tableData}
        bodyCellPadding={8}
        headerTextSize="small"
        columnLabelOverrides={columnLabelOverrides}
        columnHeaderLabel={columnHeaderLabel}
        horizontalLines
        verticalLines
        border
        className={styles.scorecardsTable}
        scrollMode={scrollMode}
        style={{width: '100%'}}
      >
        <Table.Header />
        <Table.Body>
          {loading ? (
            <Table.Loading rowCount={SCORECARD_LOADING_ROW_COUNT} />
          ) : (
            sections.flatMap((section) => {
              if (!showSectionHeaders) {
                return section.metrics.map((metric) => renderRow(metric));
              }

              return buildGroupedScorecardSections(section).flatMap((group) => {
                const sectionMetric = group.metrics.find((metric) => metric.isPillarNorthStar);
                const childMetrics = sectionMetric
                  ? group.metrics.filter((metric) => metric !== sectionMetric)
                  : group.metrics;
                const collapseKey = getGroupedScorecardSectionExpansionKey(section.id, group.id);
                const isExpanded = expandedSectionIds.has(collapseKey);
                const sectionMetricNotesExpansionKey = sectionMetric
                  ? getScorecardNotesExpansionKey(sectionMetric, snapshotIndex, snapshotKey)
                  : undefined;
                return [
                  <ScorecardSectionRow
                    key={`section-row-${collapseKey}`}
                    section={group}
                    columnCount={columnCount}
                    metric={sectionMetric}
                    hasChildRows={childMetrics.length > 0}
                    isExpanded={isExpanded}
                    onToggleExpanded={() => toggleSectionExpanded(collapseKey)}
                    snapshotIndex={snapshotIndex}
                    snapshotKey={snapshotKey}
                    priorSnapshotIndex={priorSnapshotIndex}
                    priorDateLabel={effectivePriorDateLabel}
                    isEndOfQuarter={isEndOfQuarter}
                    showCurrentUpdatedAt={showCurrentUpdatedAt}
                    notesExpanded={
                      sectionMetricNotesExpansionKey
                        ? expandedNotesRowIds.has(sectionMetricNotesExpansionKey)
                        : false
                    }
                    onNotesExpandedChange={(expanded) => {
                      if (sectionMetric) {
                        setMetricNotesExpanded(sectionMetric, expanded);
                      }
                    }}
                  />,
                  ...(isExpanded ? childMetrics.map((metric) => renderRow(metric)) : []),
                ];
              });
            })
          )}
        </Table.Body>
      </Table>
    </Box>
  );
};
